<template>
  <div
    ref="card"
    class="content-card"
    :style="styles"
    :class="[mobileCardClasses, {
      'content-card--mobile-padding': content.mobile_use_padding,
      'content-card--padding': content.use_padding
    }]"
  >
    <div class="content-card__wrapper">
      <video-player
        v-if="content.video && assetType === 'video'"
        class="content-card__media"
        controls-alignment="right"
        is-autoplay
        :video="content.video"
        :mobile-video="content.mobile_video"
      >
        <template #image>
          <art-direction
            class="content-card__media"
            :image-desktop="content.video.previewImage || content.mobile_video?.previewImage"
            :image="content.mobile_video?.previewImage || content.video.previewImage"
            :use-image-loading="false"
            :breakpoint-sizes="breakpointSizes"
            :padding="false"
          />
        </template>

        <content-card-text :content="content" />
      </video-player>

      <content-card-text
        v-else
        :content="content"
      >
        <art-direction
          v-if="content.image"
          class="content-card__media"
          :image-desktop="content.image || content.mobile_image"
          :image="content.mobile_image || content.image"
          :use-image-loading="useImageLoading"
          :breakpoint-sizes="breakpointSizes"
          :padding="false"
        />
      </content-card-text>

      <content-card-stl
        v-if="content.enable_stl"
        :content="content"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Content card
 * -----------------------------------------------------------------------------
 * Content card used for the main collection
 *
 * @param {Object} content - Content card Liquid object.
 * @param {Boolean} useImageLoading - Use image loaded animation
 */
import { mapActions, mapState } from 'vuex'

import ArtDirection from '~global/images/art-direction'

import VideoPlayer from '~async/video-player/video-player'
import ContentCardText from '~async/main-collection/content-card/content-card-text'
import ContentCardStl from '~async/main-collection/content-card/content-card-stl'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ContentCard',

  components: {
    ArtDirection,
    ContentCardText,
    ContentCardStl,
    VideoPlayer,
  },

  props: {
    content: {
      type: Object,
      default: () => ({
        button: '',
        image: {},
        mobile_image: {},
        ratio: '',
        body: '',
        title: '',
        url: '',
        use_video: false,
        video: null,
        mobile_video: null,
        use_padding: false,
        mobile_use_padding: false,
      }),
    },
    useImageLoading: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      scroll: {
        initial: 0,
        card: 0,
      },
      collapseStl: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute asset type
     * @returns {string}
     */
    assetType() {
      const {
        use_video: useVideo,
        mobile_use_video: useMobileVideo,
      } = this.content

      let type = useVideo ? 'video' : 'image'
      if (this.isMobile) {
        type = useMobileVideo ? 'video' : 'image'
      }
      return type
    },

    /**
     * Compute breakpoint sizes
     * @returns {string}
     */
    breakpointSizes() {
      const sizes = [390, 768, 1024, 1440, 1920, 2560]

      sizes.forEach((size, index) => {
        const isMobile = size <= breakpoints.l
        const col = Number(isMobile ? this.content.mobile_col : this.content.col)
        const width = isMobile ? col / 2 : col / 4
        sizes[index] = Math.round(size * width)
      })

      return sizes.join('-')
    },

    /**
     * Compute content card ID
     * @returns {String}
     */
    cardId() {
      return this.content.anchor || this.content.id
    },

    /**
     * Compute mobile card classes to adjust aspect ratio
     * @returns {string}
     */
    mobileCardClasses() {
      const { mobile_col, mobile_row } = this.content
      // eslint-disable-next-line camelcase
      return `content-card--${mobile_col}by${mobile_row}`
    },

    /**
     * Compute desktop grid row and column span
     * Dynamically compute aspect ratios
     * @returns {Object}
     */
    styles() {
      /* eslint-disable camelcase */
      const { mobile_col, mobile_row, col, row, ratio_height, mobile_ratio_height } = this.content
      const spanCol = this.isMobile ? mobile_col : col
      const spanRow = this.isMobile ? mobile_row : row
      const colGap = (spanCol - 1) * 48
      const rowGap = (spanRow - 1) * 40

      let styles = {
        gridColumn: `span ${spanCol * 3}`,
        gridRow: `span ${spanRow}`,
      }

      if (!this.isMobile) {
        styles = {
          ...styles,
          '--ratio-width': (300 * spanCol) + colGap,
          '--ratio-height': (386 * spanRow) + rowGap,
        }
      }

      if (ratio_height > 0 && !this.isMobile) {
        styles['--ratio-height'] = ratio_height
      }

      if (mobile_ratio_height > 0 && this.isMobile) {
        styles['--ratio-height'] = mobile_ratio_height
      }

      /* eslint-enable camelcase */
      return styles
    },
  },

  mounted() {
    this.scrollIntoView()
  },

  methods: {

    /**
     * Scroll into view if hash matches content card id
     */
    async scrollIntoView() {
      this.scroll.initial = window.scrollY
      const hash = location.hash
      if (hash !== `#${this.cardId}` || !this.$refs.card) {
        return
      }

      await cnvs.Sleep(this.$timing('quick'))

      this.$refs.card.scrollIntoView()
      this.scroll.card = window.scrollY

      if (this.scroll.initial === this.scroll.card) {
        return
      }

      window.addEventListener('scroll', this.handleScrollJump)

      setTimeout(() => {
        window.removeEventListener('scroll', this.handleScrollJump)
      }, 3000)
    },

    /**
     * For some reason, the page always jumps to the initial scroll position...
     */
    handleScrollJump() {
      if (this.scroll.initial === window.scrollY) {
        this.$refs.card.scrollIntoView()
        window.removeEventListener('scroll', this.handleScrollJump)
      }
    },
  },
}
</script>

<style lang="scss">
@import './content-card';
</style>
